// import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import axios from "axios";
import Stack from "@mui/material/Stack";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import Page from "src/components/Page";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useTransactions } from "../transactions.hook";
import { transactionsListColumns } from "./TransactionsList.constants";
import { useNavigate } from "react-router-dom";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import React, { useState, useEffect, useMemo } from "react";
import {
  yearFirstDateTimeFormat,
  fTransactionYearFirstDateTime,
  fConvertTransactionToLocal,
  fIsRazorpayTransaction,
} from "src/utils/formatTime";
import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Chip,
  Paper,
  Skeleton,
  CircularProgress,
  Box as MuiBox,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Token from "../../../util/Token";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import { getTeamPayout } from "src/react-query/endPoints";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import { RootTabStyle, RootTabItemStyles } from "src/theme/overrides/Tabs";
import { UpiTransactionsTab } from "./UpiTransactionsTab";
import { PayoutsTab } from "./PayoutTab";
import { CompactFilter } from "./CompactFilter";
import { Icon } from "@iconify/react";
import fileTextFill from "@iconify/icons-eva/file-text-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import creditCardFill from "@iconify/icons-eva/credit-card-fill";
import chargingFill from "@iconify/icons-eva/charging-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import expandMoreFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import expandLessFill from "@iconify/icons-eva/arrow-ios-upward-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.customShadows.z8,
  },
}));

const MetricCard = styled(Box)(({ theme, color }) => {
  const getCardColors = () => {
    switch (color) {
      case "success":
        return {
          main: theme.palette.success.main,
          light: theme.palette.success.light,
          lighter: theme.palette.success.lighter,
        };
      case "warning":
        return {
          main: theme.palette.warning.main,
          light: theme.palette.warning.light,
          lighter: theme.palette.warning.lighter,
        };
      case "error":
        return {
          main: theme.palette.error.main,
          light: theme.palette.error.light,
          lighter: theme.palette.error.lighter,
        };
      case "info":
        return {
          main: theme.palette.info.main,
          light: theme.palette.info.light,
          lighter: theme.palette.info.lighter,
        };
      case "secondary":
        return {
          main: theme.palette.secondary.main,
          light: theme.palette.secondary.light,
          lighter: theme.palette.secondary.lighter,
        };
      default:
        return {
          main: theme.palette.primary.main,
          light: theme.palette.primary.light,
          lighter: theme.palette.primary.lighter,
        };
    }
  };

  const colors = getCardColors();

  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${colors.lighter}15 50%, ${colors.light}08 100%)`,
    border: `1px solid ${colors.light}30`,
    textAlign: "center",
    height: "100%",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    // Transitions removed
    "&:hover": {
      // Hover effects removed
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.light} 100%)`,
      borderRadius: `${theme.shape.borderRadius * 1.5}px ${
        theme.shape.borderRadius * 1.5
        }px 0 0`,
    },
  };
});

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}10)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
  border: `1px solid ${theme.palette.divider}`,
  position: "relative",
  overflow: "hidden",
  height: "140px", // Fixed height for all cards
  minHeight: "140px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const SearchSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const LoadingContainer = styled(MuiBox)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "400px",
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

const LoadingIcon = styled(Icon)(({ theme }) => ({
  // Animation removed
}));

export const TransactionsList = () => {
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const isReadOnly = useAccess(FEATURE.TRANSACTION, "R");
  const hasCreateAccess = useAccess(FEATURE.CHARGER, "C");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState([]);

  // Custom filter state (used to build backend filters)
  const [customFilters, setCustomFilters] = useState([]);
  const [logicalOperator, setLogicalOperator] = useState("AND");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    setSearchValue(searchText);
  }, [searchText]);

  // Reset page when search value changes
  useEffect(() => {
    setPage(0);
  }, [searchValue]);

  const [isExporting, setIsExporting] = useState(false);

  // Map frontend operator names to backend operator names used by transactionFilterBuilder
  const mapOperatorToBackend = (operator) => {
    switch (operator) {
      case "contains":
        return "contains";
      case "notContains":
        return "does_not_contain";
      case "equals":
      case "is":
        return "equals";
      case "notEquals":
      case "notEqual":
        return "not_equals";
      case "startsWith":
        return "starts_with";
      case "endsWith":
        return "ends_with";
      case "isEmpty":
        return "is_empty";
      case "isNotEmpty":
        return "is_not_empty";
      case "isAnyOf":
        return "is_any_of";
      case "greaterThan":
      case ">":
        return "greater_than";
      case "greaterThanOrEqual":
      case ">=":
        return "greater_than_or_equal";
      case "lessThan":
      case "<":
        return "less_than";
      case "lessThanOrEqual":
      case "<=":
        return "less_than_or_equal";
      case "after":
        return "after";
      case "onOrAfter":
        return "on_or_after";
      case "before":
        return "before";
      case "onOrBefore":
        return "on_or_before";
      case "between":
        return "between";
      default:
        return null;
    }
  };

  // Build backend filter config (logic + filters) from UI filters
  const backendFiltersConfig = useMemo(() => {
    if (!customFilters || customFilters.length === 0) return null;

    const mappedFilters = customFilters
      .map((filter) => {
        if (!filter.field || !filter.operator) return null;

        const backendOperator = mapOperatorToBackend(filter.operator);
        if (!backendOperator) return null;

        // Operators that don't require a value
        const noValueOperators = ["is_empty", "is_not_empty"];

        const backendFilter = {
          field: filter.field,
          operator: backendOperator,
        };

        if (!noValueOperators.includes(backendOperator)) {
          backendFilter.value = filter.value;
        }

        return backendFilter;
      })
      .filter(Boolean);

    if (!mappedFilters.length) return null;

    return {
      logic: logicalOperator === "OR" ? "ANY" : "ALL",
      filters: mappedFilters,
    };
  }, [customFilters, logicalOperator]);

  const serializedFilters =
    backendFiltersConfig && backendFiltersConfig.filters.length
      ? JSON.stringify(backendFiltersConfig)
      : undefined;

  const {
    data: responseData = [],
    isFetching,
    refetch,
  } = useTransactions({
    teamId,
    page: page + 1, // API expects 1-based index
    limit: pageSize,
    filters: serializedFilters,
    search: searchValue,
    sort: sortModel[0]?.field,
    order: sortModel[0]?.sort,
  });

  // Extract transactions and total count based on response structure
  const transactionsData =
    responseData?.data || (Array.isArray(responseData) ? responseData : []);
  const totalTransactionsCount =
    responseData?.total ||
    (Array.isArray(responseData) ? responseData.length : 0);

  const initialColumns = transactionsListColumns(isReadOnly).filter(
    ({ field }) => field !== "notes"
  );
  const finalTableColumns = initialColumns.filter(
    ({ field }) => field !== "notes" && field !== "totalKw"
  );

  // Overview collapse state
  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);

  // const [selectedMonth, setSelectedMonth] = useState("");
  // const [selectedYear, setSelectedYear] = useState("");
  // const [payoutData, setPayoutData] = useState(null);
  // const [payoutError, setPayoutError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("transactions");

  // const currentDate = new Date();
  // const currentYear = currentDate.getFullYear();
  // const currentMonth = currentDate.getMonth(); // 0-indexed (0=Jan, 11=Dec)

  // const yearOptions = useMemo(() => {
  //   const years = [];
  //   for (let year = 2023; year <= currentYear; year++) {
  //     years.push(year);
  //   }
  //   return years;
  // }, [currentYear]);

  // const monthOptions = useMemo(() => {
  //   const months = [
  //     { value: 1, label: "January" },
  //     { value: 2, label: "February" },
  //     { value: 3, label: "March" },
  //     { value: 4, label: "April" },
  //     { value: 5, label: "May" },
  //     { value: 6, label: "June" },
  //     { value: 7, label: "July" },
  //     { value: 8, label: "August" },
  //     { value: 9, label: "September" },
  //     { value: 10, label: "October" },
  //     { value: 11, label: "November" },
  //     { value: 12, label: "December" },
  //   ];

  //   if (selectedYear === currentYear) {
  //     return months.filter((m) => m.value < currentMonth + 1);
  //   }

  //   return months;
  // }, [selectedYear, currentYear, currentMonth]);

  // Removed row click redirect - only transaction ID has hyperlink now

  const handleSearching = (event) => {
    setSearchText(event.target.value);
  };

  // Custom filter handlers
  const handleCustomFiltersChange = (filters, operator) => {
    setCustomFilters(filters);
    setLogicalOperator(operator);
  };

  const handleApplyCustomFilters = () => {
    // No-op: filters are applied on the backend via query params
  };

  const handleClearCustomFilters = () => {
    setCustomFilters([]);
    setLogicalOperator("AND");
    // Clear search text as well
    setSearchText("");
  };

  // const handleClearFilters = () => {
  //   // Clear filters clicked
  //   setFilterModel({ items: [] });
  //   setSearchText("");
  // };

  // const handleFetchPayout = async () => {
  //   if (!selectedYear || selectedMonth === "") {
  //     alert("Please select both month and year to fetch payout data");
  //     return;
  //   }

  //   try {
  //     setPayoutError(null);
  //     const token = Token.get();

  //     const response = await axios.get(
  //       getTeamPayout(
  //         typeof teamId === "object" ? teamId.id : teamId, // make sure it’s primitive
  //         Number(selectedYear),
  //         Number(selectedMonth)
  //       ),
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     // Axios Payout Response
  //     setPayoutData(response.data);
  //   } catch (error) {
  //     console.error("Axios Payout Error:", error);
  //     setPayoutError(error);
  //   }
  // };

  // const handleMonthChange = (event) => {
  //   setSelectedMonth(event.target.value);
  //   setPayoutData(null);
  // };

  // const handleYearChange = (event) => {
  //   setSelectedYear(event.target.value);
  //   setSelectedMonth("");
  //   setPayoutData(null);
  // };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // const handleDownloadPDF = () => {
  //   if (!payoutData?.data) {
  //     alert("No payout data to download");
  //     return;
  //   }

  //   const doc = new jsPDF();
  //   const { teamDetails, summary, chargerSummary } = payoutData.data;
  //   const acc = teamDetails.payoutAccounts?.[0];

  //   const selectedMonthLabel =
  //     monthOptions.find((m) => m.value === selectedMonth)?.label || "";
  //   const invoicePeriod = `${selectedMonthLabel} ${selectedYear}`;

  //   doc.setFontSize(16);
  //   doc.text("Payout Invoice", 14, 20);

  //   doc.setFontSize(11);
  //   let y = 40;
  //   doc.text(`Beneficiary: ${acc?.beneficiary_name || "-"}`, 14, y);
  //   doc.text(`Business: ${acc?.business_name || "-"}`, 14, y + 6);
  //   doc.text(`Business Type: ${acc?.business_type || "-"}`, 14, y + 12);
  //   doc.text(`Phone: ${acc?.phone || "-"}`, 14, y + 18);
  //   doc.text(`Email: ${acc?.email || "-"}`, 14, y + 24);
  //   doc.text(`GST: ${acc?.gst || "-"}`, 14, y + 30);

  //   doc.setFontSize(11);
  //   let rightX = 140;
  //   doc.text(
  //     `Invoice Date: ${new Date().toLocaleDateString("en-IN")}`,
  //     rightX,
  //     y
  //   );
  //   doc.text(`Team: ${teamDetails?.name || "-"}`, rightX, y + 6);
  //   doc.text(`Period: ${invoicePeriod}`, rightX, y + 12);

  //   autoTable(doc, {
  //     startY: 85,
  //     head: [
  //       [
  //         "Transactions",
  //         "Chargers",
  //         "kWh",
  //         "Total Collection",
  //         "Amik Charges",
  //         "Total Payout",
  //       ],
  //     ],
  //     body: [
  //       [
  //         summary.totalTransactions,
  //         summary.totalChargers,
  //         summary.totalKW.toFixed(3),
  //         `Rs ${summary.totalCollectionWithoutPGC.toFixed(2)}`,
  //         `Rs ${summary.totalAmikServiceCharges.toFixed(2)}`,
  //         `Rs ${summary.totalPayoutAmount.toFixed(2)}`,
  //       ],
  //     ],
  //   });

  //   const chargerRows = Object.entries(chargerSummary).map(([id, c]) => [
  //     id,
  //     c.charger_type,
  //     c.totalKW.toFixed(3) + " kWh",
  //     `Rs ${c.totalCollectionWithoutPGC.toFixed(2)}`,
  //     `Rs ${c.maxAmikServiceCharges.toFixed(2)}`,
  //   ]);

  //   autoTable(doc, {
  //     startY: doc.lastAutoTable.finalY + 10,
  //     head: [
  //       ["Charger ID", "Type", "kWh", "Collection (Rs)", "Amik Charges (Rs)"],
  //     ],
  //     body: chargerRows,
  //   });

  //   doc.save(`Payout_Invoice_${teamDetails.name}_${invoicePeriod}.pdf`);
  // };

  // Convert data once
  // Convert data once
  const convertedData = useMemo(() => {
    if (!isFetching && transactionsData.length) {
      return transactionsData.map((transaction) => {
        let processedTxn = { ...transaction };

        // Convert payment_type to UPI if paymentDescription matches criteria
        if (
          processedTxn.paymentDescription ===
          "Paytm Payment TXN_SUCCESS for UPI" ||
          processedTxn.paymentDescription === "QRv2 Payment" ||
          processedTxn.paymentDescription?.toUpperCase().includes("UPI")
        ) {
          processedTxn.payment_type = "UPI";
        }

        if (fIsRazorpayTransaction(processedTxn)) {
          return processedTxn; // Skip conversion for Razorpay transactions
        }
        return fConvertTransactionToLocal(processedTxn);
      });
    }
    return [];
  }, [isFetching, transactionsData]);

  // Compute filter UI state (for chips / badges)
  const activeFiltersCount = useMemo(
    () =>
      customFilters.filter(
        (filter) =>
          filter.field &&
          filter.operator &&
          (filter.value || ["isEmpty", "isNotEmpty"].includes(filter.operator))
      ).length,
    [customFilters]
  );

  const isCustomFiltered = activeFiltersCount > 0;

  // Backend now handles search filtering
  const finalFilteredData = convertedData;

  // Sort and update table data
  const sortedData = useMemo(() => {
    return finalFilteredData;
  }, [finalFilteredData]);

  // Update table data and counts - use sortedData directly instead of useEffect
  const tableData = sortedData;
  const filteredCount = sortedData.length;

  // Note: Auto-collapse only happens when user clicks "Add Filter" button
  // Users can manually expand/collapse the overview at any time

  const {
    totalRevenue,
    totalEnergy,
    totalChargers,
    totalCardRevenue,
    totalWalletRevenue,
    totalRfidRevenue,
    totalTransactions,
    activeTransactions,
    completedTransactions,
  } = useMemo(() => {
    let revenue = 0;
    let energy = 0;
    const chargerSet = new Set();
    let cardRevenue = 0;
    let walletRevenue = 0;
    let rfidRevenue = 0;
    let activeCount = 0;
    let completedCount = 0;

    tableData.forEach((txn) => {
      const start = parseFloat(txn.start_meter_value || 0);
      const stop = parseFloat(txn.stop_meter_value || 0);
      const current = parseFloat(txn.current_meter_value || 0);

      // totalAmountCharged is already the net amount (refunds already deducted)
      const netAmount = parseFloat(txn.totalAmountCharged || 0);

      // Only add positive amounts to revenue
      if (netAmount > 0) {
        revenue += netAmount;
        if (txn.payment_type === "CARD") {
          cardRevenue += netAmount;
        } else if (txn.payment_type === "WALLET") {
          walletRevenue += netAmount;
        } else if (txn.payment_type === "RFID") {
          rfidRevenue += netAmount;
        }
      }

      // Use stop meter value if available, otherwise use current meter value
      const endValue = stop > 0 ? stop : current;
      if (!isNaN(start) && !isNaN(endValue) && endValue > start) {
        energy += (endValue - start) / 1000;
      }
      if (txn.chargepoint_id) chargerSet.add(txn.chargepoint_id);

      // Count active vs completed transactions
      if (!txn.stoptime || txn.stoptime === null || txn.stoptime === "") {
        activeCount++;
      } else {
        completedCount++;
      }
    });

    return {
      totalRevenue: revenue.toFixed(2),
      totalEnergy: energy.toFixed(2),
      totalChargers: chargerSet.size,
      totalCardRevenue: cardRevenue.toFixed(2),
      totalWalletRevenue: walletRevenue.toFixed(2),
      totalRfidRevenue: rfidRevenue.toFixed(2),
      totalTransactionCount: tableData.length,
      serverTotalTransactions: totalTransactionsCount,
      activeTransactions: activeCount,
      completedTransactions: completedCount,
    };
  }, [tableData, totalTransactionsCount]);

  return (
    <Page>
      <TabContext value={selectedTab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: "100%", mb: 3 }}
        >
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={3}
            onChange={handleTabChange}
          >
            <RootTabItemStyles value="transactions" label="Transactions" />
            <RootTabItemStyles value="upi" label="Payments" />
            <RootTabItemStyles value="payout" label="Payouts" />
          </RootTabStyle>
        </Stack>

        <Card sx={{ p: 2 }}>
          <TabPanel value="transactions">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            />

            {/* Header Section */}
            <HeaderSection>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Icon
                    icon={fileTextFill}
                    width={32}
                    height={32}
                    style={{ marginRight: 12, color: "#4A90E2" }}
                  />
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background:
                          "linear-gradient(135deg, #4A90E2 0%, #7DD3FC 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 0.5,
                      }}
                    >
                      Transaction Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monitor and manage all charging transactions
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isCustomFiltered && (
                    <Chip
                      label="Filtered View"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  )}

                  {isCustomFiltered && isOverviewCollapsed && (
                    <Chip
                      label="Click to show overview"
                      size="small"
                      color="info"
                      variant="filled"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        "&:hover": {
                          bgcolor: "info.dark",
                        },
                      }}
                      onClick={() => setIsOverviewCollapsed(false)}
                      style={{ cursor: "pointer" }}
                    />
                  )}

                  <Tooltip
                    title={
                      isOverviewCollapsed
                        ? "Show overview statistics"
                        : "Hide overview statistics"
                    }
                  >
                    <IconButton
                      onClick={() =>
                        setIsOverviewCollapsed(!isOverviewCollapsed)
                      }
                      sx={{
                        color: "primary.main",
                        bgcolor: "primary.50",
                        border: "2px solid",
                        borderColor: "primary.200",
                        "&:hover": {
                          bgcolor: "primary.100",
                          color: "primary.dark",
                          borderColor: "primary.300",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Icon
                        icon={
                          isOverviewCollapsed ? expandMoreFill : expandLessFill
                        }
                        width={24}
                        height={24}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Collapse in={!isOverviewCollapsed}>
                <Grid container spacing={3} alignItems="stretch">
                  {isFetching ? (
                    // Show loading skeletons for stats cards
                    Array.from({ length: 5 }).map((_, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                              sx={{ mr: 1 }}
                            />
                            <Skeleton variant="text" width={120} height={20} />
                          </Box>
                          <Skeleton variant="text" width={80} height={40} />
                        </StatsCard>
                      </Grid>
                    ))
                  ) : (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Icon
                              icon={fileTextFill}
                              width={20}
                              height={20}
                              style={{ marginRight: 8, color: "#1976d2" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Total Transactions
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "primary.main" }}
                          >
                            {totalTransactionsCount}
                          </Typography>
                        </StatsCard>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Icon
                              icon={clockFill}
                              width={20}
                              height={20}
                              style={{ marginRight: 8, color: "#ed6c02" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Active Transactions
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "warning.main" }}
                          >
                            {activeTransactions}
                          </Typography>
                        </StatsCard>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Icon
                              icon={checkmarkCircle2Fill}
                              width={20}
                              height={20}
                              style={{ marginRight: 8, color: "#2e7d32" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Completed
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "success.main" }}
                          >
                            {completedTransactions}
                          </Typography>
                        </StatsCard>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Icon
                              icon={creditCardFill}
                              width={20}
                              height={20}
                              style={{ marginRight: 8, color: "#d32f2f" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Total Revenue
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "error.main" }}
                          >
                            ₹
                            {Number(totalRevenue) > 1000
                              ? `${(totalRevenue / 1000).toFixed(2)}k`
                              : totalRevenue}
                          </Typography>
                        </StatsCard>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.4}
                        sx={{ display: "flex" }}
                      >
                        <StatsCard sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Icon
                              icon={chargingFill}
                              width={20}
                              height={20}
                              style={{ marginRight: 8, color: "#9c27b0" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Total Chargers
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "secondary.main" }}
                          >
                            {totalChargers}
                          </Typography>
                        </StatsCard>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Collapse>
            </HeaderSection>

            {/* Month and Year Selection with Payout Data */}
            {/* {hasCreateAccess && (
            <Card sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label="Year"
                    onChange={handleYearChange}
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 120 }} disabled={!selectedYear}>
                  <InputLabel id="month-select-label">Month</InputLabel>
                  <Select
                    labelId="month-select-label"
                    value={selectedMonth}
                    label="Month"
                    onChange={handleMonthChange}
                  >
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleFetchPayout}
                  disabled={!selectedMonth || !selectedYear}
                >
                  Fetch Payout Data
                </Button>
              </Stack>

              {payoutError && (
                <Box
                  sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}
                >
                  <Typography variant="body2" color="error">
                    Error: {payoutError.message || "Failed to fetch payout data"}
                  </Typography>
                </Box>
              )}

              {payoutData?.data && (
                <Box
                  sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
                >
                  <Typography variant="h6" gutterBottom>
                    Payout Summary for{" "}
                    {monthOptions.find((m) => m.value === selectedMonth)?.label}{" "}
                    {selectedYear}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        <strong>Total Transactions:</strong>{" "}
                        {payoutData.data.summary.totalTransactions}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Chargers:</strong>{" "}
                      {payoutData.data.summary.totalChargers}
                    </Typography>
                  </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        <strong>Total Collection:</strong> ₹
                        {payoutData.data.summary.totalCollectionWithoutPGC.toFixed(
                          2
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        <strong>Amik Service Charges:</strong> ₹
                        {payoutData.data.summary.totalAmikServiceCharges.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        <strong>Total Payout:</strong> ₹
                        {payoutData.data.summary.totalPayoutAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Charger Breakdown:
                  </Typography>
                  {Object.entries(payoutData.data.chargerSummary).map(
                    ([chargerId, chargerData]) => (
                      <Box
                        key={chargerId}
                        sx={{
                          mt: 1,
                          p: 1,
                          backgroundColor: "white",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>{chargerId}:</strong> ₹
                          {chargerData.totalCollectionWithoutPGC.toFixed(2)} |{" "}
                          {chargerData.totalKW.toFixed(3)} kWh
                        </Typography>
                      </Box>
                    )
                  )}
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDownloadPDF}
                    sx={{ my: 2 }}
                  >
                    Download PDF
                  </Button>
                </Box>
              )}
            </Card>
          )} */}

            {/* Header Section */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Transactions
                  </Typography>
                  {(searchValue || isCustomFiltered) && (
                    <Chip
                      label={`${filteredCount} of ${totalTransactionsCount} transactions`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Export Button */}
                  <Tooltip title="Export transactions to Excel">
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isExporting}
                      startIcon={
                        isExporting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Icon icon={fileTextFill} width={16} height={16} />
                        )
                      }
                      onClick={async () => {
                        try {
                          setIsExporting(true);
                          const token = Token.get();
                          const {
                            exportTransactionsUrl,
                            getTeamUrl,
                          } = require("src/react-query/endPoints");

                          // Construct query params
                          const queryParams = new URLSearchParams();

                          if (searchValue) {
                            queryParams.append("search", searchValue);
                          }

                          if (serializedFilters) {
                            // serializedFilters is already a JSON string of {logic, filters}
                            // Backend expects this exact string in "filters" param
                            // But wait, in queryKeys we encode it. Axios params serializer handles some,
                            // but since we are building URL manually or passing params object:
                            // Let's pass it as a regular param to axios
                            queryParams.append("filters", serializedFilters);
                          }

                          // We can pass page/limit for context, but backend ignores them for export
                          // however, keeping them might be useful if backend logs it
                          queryParams.append("page", (page + 1).toString());
                          queryParams.append("limit", pageSize.toString());

                          if (sortModel.length > 0) {
                            queryParams.append("sort", sortModel[0].field);
                            queryParams.append("order", sortModel[0].sort);
                          }

                          const url = `${getTeamUrl(
                            teamId,
                            exportTransactionsUrl
                          )}?${queryParams.toString()}`;

                          const response = await axios.get(url, {
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          });

                          const exportData = response.data?.data || [];

                          if (!exportData || exportData.length === 0) {
                            alert("No data to export");
                            setIsExporting(false);
                            return;
                          }

                          // Convert data for Excel using existing logic
                          const processedData = exportData.map(
                            (transaction) => {
                              let processedTxn = { ...transaction };

                              // New requirement: Convert payment_type to UPI if paymentDescription matches criteria
                              if (
                                processedTxn.paymentDescription ===
                                "Paytm Payment TXN_SUCCESS for UPI" ||
                                processedTxn.paymentDescription === "QRv2 Payment" ||
                                processedTxn.paymentDescription?.toUpperCase().includes("UPI")
                              ) {
                                processedTxn.payment_type = "UPI";
                              }

                              if (fIsRazorpayTransaction(processedTxn)) {
                                return processedTxn;
                              }
                              return fConvertTransactionToLocal(processedTxn);
                            }
                          );
                          const priceIndex = initialColumns.findIndex(col => col.field === "price");
                          let excelColumns = [
                            ...initialColumns.slice(0, priceIndex + 1),
                            { field: "priceUnit", headerName: "Price Unit" },
                            ...initialColumns.slice(priceIndex + 1),
                          ];



                          const csvObject = {
                            headers: excelColumns.map((column) => column.headerName),
                            properties: excelColumns.map((column) => column.field),
                            types: excelColumns.map((column) => {
                              if (
                                /(date|time|createdAt|createAt|createdOn|createOn|updateAt|updatedAt|updateOn|updatedOn)/gim.test(
                                  column.field
                                )
                              )
                                return "date";
                              if (
                                /(amount|paid|charged|refund|kw|kwh|energy|meter|value|price|cost|total|quantity|start_meter_value|stop_meter_value)/i.test(
                                  column.field
                                )
                              )
                                return "number";
                              return "string";
                            }),
                            valuesObjArr: processedData.map((row) => {
                              let rowValue = { ...row };
                              // Add synthetic price and priceUnit fields
                              const pkw = parseFloat(row.pricePerKw || 0);
                              const pmin = parseFloat(row.pricePerMin || 0);
                              if (pkw > 0) {
                                rowValue.price = pkw;
                                rowValue.priceUnit = "/kWh";
                              } else if (pmin > 0) {
                                rowValue.price = pmin;
                                rowValue.priceUnit = "/min";
                              } else {
                                rowValue.price = 0;
                                rowValue.priceUnit = "";
                              }

                              // Calculate consumptionCost for Excel
                              const chargingCost = parseFloat(row.totalAmountCharged || 0);
                              const taxAmount = parseFloat(row.taxAmount || 0);
                              const pgCharges = parseFloat(row.transactionAmount || 0);
                              rowValue.consumptionCost = chargingCost - taxAmount - pgCharges;
                              excelColumns.forEach((column) => {
                                if (
                                  /(date|time|createdAt|createAt|createdOn|createOn|updateAt|updatedAt|updateOn|updatedOn)/gim.test(
                                    column.field
                                  )
                                ) {
                                  rowValue = {
                                    ...rowValue,
                                    [column.field]:
                                      fTransactionYearFirstDateTime(
                                        row[column.field]
                                      ),
                                  };
                                }
                                if (
                                  column.field !== "priceUnit" &&
                                  /(amount|paid|charged|refund|kw|kwh|energy|meter|value|price|cost|total|quantity|start_meter_value|stop_meter_value)/i.test(
                                    column.field
                                  )
                                ) {
                                  let numValue;
                                  if (typeof rowValue[column.field] === "string") {
                                    numValue = parseFloat(
                                      rowValue[column.field].replace(/[^0-9.-]/g, "")
                                    );
                                  } else {
                                    numValue = parseFloat(rowValue[column.field]);
                                  }
                                  rowValue = {
                                    ...rowValue,
                                    [column.field]: isNaN(numValue)
                                      ? 0
                                      : numValue,
                                  };
                                }
                              });
                              return rowValue;
                            }),
                          };
                          const {
                            saveToExcel,
                          } = require("src/utils/convertAnddownloadCsv");
                          saveToExcel(
                            csvObject,
                            `transactions_${teamId}_${
                              new Date().toISOString().split("T")[0]
                            }`
                          );
                        } catch (error) {
                          console.error("Export failed:", error);
                          alert(
                            "Failed to export transactions. Please try again."
                          );
                        } finally {
                          setIsExporting(false);
                        }
                      }}
                      sx={{
                        background:
                          "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
                        boxShadow: "0 4px 14px 0 rgba(46, 125, 50, 0.39)",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.5s ease-in-out",
                        },
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                          boxShadow: "0 6px 20px 0 rgba(46, 125, 50, 0.5)",
                          transform: "translateY(-2px)",
                          "&:before": {
                            left: "100%",
                          },
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                        "&.Mui-disabled": {
                          background:
                            "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                          color: "text.disabled",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isExporting ? "Exporting..." : "Export To Excel"}
                    </Button>
                  </Tooltip>

                  <Tooltip title="Refresh data">
                    <span>
                      <IconButton
                        onClick={() => refetch()}
                        disabled={isFetching}
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": {
                            bgcolor: "primary.dark",
                          },
                          "&:disabled": {
                            bgcolor: "grey.300",
                            color: "grey.500",
                          },
                        }}
                      >
                        <Icon icon={refreshFill} width={20} height={20} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Stack>

            {/* Combined Search, Filter and Table Card */}
            <StyledListCard>
              <CompactFilter
                filters={customFilters}
                onFiltersChange={handleCustomFiltersChange}
                onApplyFilters={handleApplyCustomFilters}
                onClearFilters={handleClearCustomFilters}
                searchText={searchText}
                onSearchChange={setSearchText}
                onExpandFilter={() => setIsOverviewCollapsed(true)}
              />
              <BaseTable
                isReadOnly={isReadOnly}
                rows={tableData ?? []}
                columns={finalTableColumns}
                loading={isFetching}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={(newModel) => setSortModel(newModel)}
                rowCount={totalTransactionsCount}
                page={page}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                getRowId={(row) => row.transactionId}
                height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
                disableColumnFilter={true}
                sx={{
                  "& .MuiDataGrid-root": {
                    border: "none",
                  },
                  "& .MuiDataGrid-main": {
                    height: "auto !important",
                    minHeight: "auto !important",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    height: "auto !important",
                    minHeight: "auto !important",
                  },
                  "& .MuiDataGrid-row": {
                    height: "52px !important",
                  },
                  "& .MuiDataGrid-cell": {
                    height: "52px !important",
                  },
                }}
              />
            </StyledListCard>
          </TabPanel>

          <TabPanel value="upi">
            <UpiTransactionsTab />
          </TabPanel>

          <TabPanel value="payout">
            <PayoutsTab />
          </TabPanel>
        </Card>
      </TabContext>
    </Page>
  );
};
